"use client";

import React, { useEffect, useState } from 'react';
import { useApp } from '@/pages/providers/AppProvider';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
// Select component removed (no category filter)
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Calendar,
  User,
  Clock,
  Search,
  TrendingUp,
  Briefcase,
  Users,
  BookOpen,
  ArrowRight,
  ArrowLeft,
  Tag,
  Share2,
  Mail,
  PlayCircle
} from 'lucide-react';
import { HeroCarousel } from '../components/HeroCarousel';
import { normalizeMediaUrl } from '@/utils/media';
import { apiClient } from '@/lib/api-client';
import { SEO } from '../components/SEO';

interface BlogsPageProps {
  onNavigate?: (page: string) => void;
}

export function BlogsPage({ onNavigate }: BlogsPageProps) {
  const { contentVersion } = useApp();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedArticle, setSelectedArticle] = useState<any>(null);
  const [isArticleModalOpen, setIsArticleModalOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const [persistedPosts, setPersistedPosts] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const PAGE_SIZE = 4;
  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const res = await apiClient.getBlogs({ status: 'published', limit: 20 });
        const incoming = Array.isArray(res?.blogs) ? res.blogs : (Array.isArray(res) ? res : []);
        const filtered = (Array.isArray(incoming) ? incoming : []).filter((b: any) => {
          const createdRole = String(
            b?.created_by_role || b?.createdByRole || b?.creator_role || b?.role || b?.user_type || ''
          ).trim().toLowerCase();
          const source = String(b?.source || b?.origin || '').trim().toLowerCase();
          if (createdRole && createdRole !== 'super_admin') return false;
          if (source && source !== 'admin' && source !== 'super_admin') return false;
          return true;
        });
        setPersistedPosts(filtered);
        setError(null);
      } catch (err: any) {
        setError(err?.message || 'Failed to load blog posts');
      } finally {
        setIsLoading(false);
      }
    })();
  }, [contentVersion]);

  const getDisplayDate = (p: any) => {
    try {
      if (p?.publishDate) return String(p.publishDate);
      if (p?.published_at) {
        const raw = String(p.published_at);
        const isoCandidate = raw.includes(' ') ? raw.replace(' ', 'T') + 'Z' : raw;
        const d = new Date(isoCandidate);
        if (!isNaN(d.getTime())) return d.toLocaleDateString();
        return raw;
      }
    } catch { }
    return '';
  };

  // Resolve the best image source for a blog post
  const getYouTubeVideoId = (url: string) => {
    try {
      const u = String(url || '').trim();
      if (!u) return '';
      const m1 = u.match(/[?&]v=([a-zA-Z0-9_-]{6,})/); // watch?v=
      if (m1 && m1[1]) return m1[1];
      const m2 = u.match(/youtu\.be\/([a-zA-Z0-9_-]{6,})/); // youtu.be/
      if (m2 && m2[1]) return m2[1];
      const m3 = u.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{6,})/); // embed/
      if (m3 && m3[1]) return m3[1];
      const m4 = u.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{6,})/); // shorts/
      if (m4 && m4[1]) return m4[1];
      return '';
    } catch {
      return '';
    }
  };

  const getImageSrc = (p: any) => {
    const type = String(p?.type || '').trim().toLowerCase();
    const cover = p?.cover_image_url || p?.image || '';
    const firstImage = Array.isArray(p?.image_urls) && p.image_urls.length ? String(p.image_urls[0]) : '';
    const youtubeId = getYouTubeVideoId(String(p?.video_url || (Array.isArray(p?.video_urls) ? p.video_urls[0] : '') || ''));
    if (type === 'youtube' || youtubeId) {
      const thumb = youtubeId ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg` : '';
      const normalizedThumb = normalizeMediaUrl(thumb);
      return normalizedThumb || thumb || (normalizeMediaUrl(cover) || cover) || (normalizeMediaUrl(firstImage) || firstImage) || '/images/placeholder.png';
    }
    const prioritized = cover || firstImage;
    const normalized = normalizeMediaUrl(prioritized);
    return normalized || prioritized || '/images/placeholder.png';
  };

  const resolveVideoUrl = (url: string) => {
    const id = getYouTubeVideoId(url);
    return id ? `https://www.youtube.com/watch?v=${id}` : url;
  };

  const getPrimaryVideoUrl = (p: any) => {
    const v1 = String(p?.video_url || '').trim();
    if (v1) return v1;
    const arr = Array.isArray(p?.video_urls) ? p.video_urls : [];
    const v2 = arr.length ? String(arr[0] || '') : '';
    return v2;
  };

  const handleOpenVideo = (p: any) => {
    const raw = getPrimaryVideoUrl(p);
    const url = resolveVideoUrl(raw);
    if (url) window.open(url, '_blank');
  };

  const openImagePreview = (url: string) => {
    const src = normalizeMediaUrl(url) || url;
    setPreviewImage(src || null);
  };

  const stripHtml = (html: string) => {
    try {
      const div = document.createElement('div');
      div.innerHTML = String(html || '');
      return div.textContent || div.innerText || '';
    } catch {
      return String(html || '');
    }
  };

  const getExcerpt = (p: any, max = 160) => {
    const raw = String(p?.excerpt || '').trim();
    const base = raw || String(p?.content || '');
    const text = stripHtml(base);
    return text.length > max ? text.slice(0, max) + '…' : text;
  };

  // Prefer explicit description if available; fall back to excerpt/content
  const getDescriptionSnippet = (p: any, max = 180) => {
    const desc = stripHtml(String(p?.description || '').trim());
    const fallback = getExcerpt(p, max);
    const text = desc || fallback;
    return text.length > max ? text.slice(0, max) + '…' : text;
  };

  // Overview description: prefer explicit description, then excerpt, then stripped content
  const getOverviewDescription = (p: any) => {
    const desc = stripHtml(String(p?.description || '').trim());
    if (desc) return desc;
    const ex = stripHtml(String(p?.excerpt || '').trim());
    if (ex) return ex;
    return stripHtml(String(p?.content || ''));
  };

  // Static mock blog posts removed entirely — page shows only backend content

  // Use backend-only data; default to empty array when none
  const posts = Array.isArray(persistedPosts) ? persistedPosts : [];

  // Categories memo removed

  // Remove featured posts section on public site
  const recentPosts = React.useMemo(() => posts.slice(0, 5), [posts]);

  const filteredPosts = React.useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    return posts.filter((post: any) => {
      const title = String(post?.title || '').toLowerCase();
      const excerpt = String(post?.excerpt || '').toLowerCase();
      const tags: string[] = Array.isArray(post?.tags) ? post.tags.map((t: any) => String(t)) : [];
      const matchesSearch = !term || title.includes(term) || excerpt.includes(term) || tags.some(t => t.toLowerCase().includes(term));
      return matchesSearch;
    });
  }, [posts, searchTerm]);

  const handleReadMore = (article: any) => {
    setSelectedArticle(article);
    setIsArticleModalOpen(true);
  };



  const handleShareArticle = (article: any) => {
    if (!article) {
      return;
    }

    const articleUrl = `${window.location.origin}/blogs#article-${article.id}`;

    // Use only reliable fallback method to avoid clipboard API permission issues
    fallbackCopyArticle(articleUrl);
  };

  const fallbackCopyArticle = (text: string) => {
    try {
      // Use input element for better compatibility
      const tempInput = document.createElement('input');
      tempInput.type = 'text';
      tempInput.value = text;

      // Position off-screen but accessible
      tempInput.style.position = 'absolute';
      tempInput.style.left = '-9999px';
      tempInput.style.top = '0';
      tempInput.style.opacity = '0';
      tempInput.style.pointerEvents = 'none';
      tempInput.setAttribute('readonly', '');

      document.body.appendChild(tempInput);

      // Select and copy
      tempInput.focus();
      tempInput.setSelectionRange(0, tempInput.value.length);
      tempInput.select();

      let successful = false;
      try {
        successful = document.execCommand('copy');
      } catch (execError) {
        console.warn('execCommand failed:', execError);
        successful = false;
      }

      document.body.removeChild(tempInput);

      if (successful) {
        // Dynamic import to avoid issues
        import('sonner').then(({ toast }) => {
          toast.success('Article link copied to clipboard!');
        }).catch(() => {
          console.log('Article link copied to clipboard!');
        });
      } else {
        showArticleUrlDialog(text);
      }
    } catch (err) {
      console.error('Copy operation failed:', err);
      showArticleUrlDialog(text);
    }
  };

  const showArticleUrlDialog = (url: string) => {
    const message = `Copy this article link:\n\n${url}`;
    if (window.prompt) {
      window.prompt(message, url);
    } else {
      alert(message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Career Blog & Insights - MegaJobNepal"
        description="Read the latest career advice, job market trends, and interview tips from MegaJobNepal experts. Stay updated with job opportunities in Nepal."
        type="article"
      />
      {isLoading && (
        <div className="responsive-container py-6"><div className="text-center text-gray-600">Loading blog posts...</div></div>
      )}
      {error && !isLoading && (
        <div className="responsive-container py-6"><div className="text-center text-red-600">{error}</div></div>
      )}
      <HeroCarousel />
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-8 sm:py-10">
        <div className="responsive-container">
          <div className="text-center">
            <h1 className="hero-title mb-2 drop-shadow-sm">Career Insights & Tips</h1>
            <p className="max-w-2xl mx-auto mb-4 text-white/90 drop-shadow-sm text-base sm:text-lg lg:text-xl mt-2 sm:mt-3">
              Expert advice, industry insights, and career guidance to help you succeed
              in Nepal's dynamic job market.
            </p>

            {/* Search Bar */}
            <div className="max-w-md mx-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                <Input
                  type="text"
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="mobile-input pl-10 bg-white text-gray-900"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content + Sidebar inline */}
      <section className="section-padding bg-gray-50">
        <div className="full-width-container">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-6 items-start">
            {/* Main Articles */}
            <div className="lg:col-span-3">
              {/* Filter Bar removed — only search term result count */}
              {searchTerm && (
                <p className="text-xs sm:text-sm text-gray-600 mb-6 sm:mb-8">
                  Found {filteredPosts.length} article(s) for "{searchTerm}"
                </p>
              )}

              <div className="mb-6 sm:mb-8">
                <div className="flex items-end justify-between">
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Our Recent Articles</h2>
                    <p className="text-sm text-gray-500">Stay Informed with Our Latest Insights</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 lg:gap-4">
                {filteredPosts.slice(pageIndex * PAGE_SIZE, pageIndex * PAGE_SIZE + PAGE_SIZE).map((post, index) => (
                  <Card key={`ui-article-${post?.id}-${index}`} className="overflow-hidden group">
                    <div className="relative w-full h-32">
                      <img
                        src={getImageSrc(post)}
                        alt={post.title}
                        className="w-full h-full object-cover cursor-zoom-in"
                        onClick={(e) => { e.stopPropagation(); openImagePreview(getImageSrc(post)); }}
                      />
                      {Boolean(getPrimaryVideoUrl(post)) && (
                        <button
                          className="absolute bottom-2 right-2 flex items-center justify-center bg-black/50 hover:bg-black/60 text-white rounded-full p-1"
                          onClick={(e) => { e.stopPropagation(); handleOpenVideo(post); }}
                          aria-label="Open Video"
                        >
                          <PlayCircle className="w-6 h-6" />
                        </button>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-blue-600 truncate max-w-[50%]">{post.author}</span>
                        <span className="text-xs text-gray-500">{getDisplayDate(post)}</span>
                      </div>
                      <h3 className="text-sm sm:text-base font-semibold text-gray-900 leading-tight line-clamp-2 mb-1">
                        {post.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-600 line-clamp-2 mb-3">
                        {getDescriptionSnippet(post, 140)}
                      </p>
                      <button className="text-blue-600 text-xs sm:text-sm font-medium inline-flex items-center gap-2" onClick={(e) => { e.stopPropagation(); handleReadMore(post); }}>
                        Read More <ArrowRight className="w-4 h-4" />
                      </button>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="flex items-center justify-center gap-3 mt-4">
                <Button
                  variant="outline"
                  onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                  disabled={pageIndex === 0}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setPageIndex((p) => (((p + 1) * PAGE_SIZE) < filteredPosts.length ? p + 1 : p))}
                  disabled={((pageIndex + 1) * PAGE_SIZE) >= filteredPosts.length}
                >
                  Next
                </Button>
                <Button
                  onClick={() => {
                    if (onNavigate) onNavigate('blogs');
                    else window.location.assign('/blogs');
                  }}
                >
                  View All Blogs
                </Button>
              </div>

              {filteredPosts.length === 0 && (
                <div className="text-center py-12">
                  <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-600 mb-2">No articles found</h3>
                  <p className="text-gray-500">Try adjusting your search or filter criteria.</p>
                </div>
              )}
            </div>
            {/* Recent Posts Sidebar */}
            <div className="space-y-4 lg:space-y-6 lg:col-span-1 lg:sticky lg:top-6 lg:self-start">
              <Card className="border shadow-sm">
                <CardHeader className="bg-green-600 text-white rounded-t-md px-3 py-2">
                  <h3 className="font-semibold text-sm flex items-center">
                    <BookOpen className="w-4 h-4 mr-2" />
                    Recent Posts
                  </h3>
                </CardHeader>
                <CardContent className="space-y-2 px-3 py-3">
                  {recentPosts.map((post, index) => (
                    <div
                      key={`recent-${post?._id || post?.id || index}-${index}`}
                      className="flex items-center gap-2 p-2 rounded-md hover:bg-green-50 border border-green-100"
                      onClick={() => handleReadMore(post)}
                    >
                      <img
                        src={getImageSrc(post)}
                        alt={post.title}
                        className="w-10 h-8 object-cover rounded-md border border-green-200"
                      />
                      <div className="min-w-0 flex-1">
                        <h4 className="text-xs font-medium text-gray-900 line-clamp-1">
                          {post.title}
                        </h4>
                        <div className="flex items-center justify-between">
                          <p className="text-[10px] text-green-700 font-medium">{getDisplayDate(post)}</p>
                          <p className="text-[10px] text-gray-600 line-clamp-1">{getDescriptionSnippet(post, 60)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Article Modal */}
      <Dialog open={isArticleModalOpen} onOpenChange={setIsArticleModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden bg-gradient-to-br from-blue-50 via-white to-indigo-50 border-2 border-blue-100 shadow-2xl mobile-card" aria-describedby="article-description">
          {selectedArticle && (
            <>
              <DialogHeader className="pb-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-lg -m-6 mb-4 p-4 sm:p-6 pr-12 sm:pr-16">
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <img
                    src={getImageSrc(selectedArticle)}
                    alt={selectedArticle.title}
                    className="w-16 h-10 sm:w-20 sm:h-12 object-cover rounded-lg border-2 border-white/20 shadow-lg"
                  />
                  <div className="flex-1 pr-2">
                    <DialogTitle className="text-lg sm:text-xl font-bold text-white leading-tight drop-shadow-sm">
                      {selectedArticle.title}
                    </DialogTitle>
                    <DialogDescription id="article-description" className="sr-only">
                      {selectedArticle.excerpt}
                    </DialogDescription>
                    <div className="flex items-center space-x-3 sm:space-x-4 mt-2">
                      <Badge variant="secondary" className="bg-white/20 text-white border-white/30 hover:bg-white/30 text-xs sm:text-sm">
                        {selectedArticle.category}
                      </Badge>
                      <div className="flex items-center space-x-1 text-xs sm:text-sm text-blue-100">
                        <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span>{selectedArticle.readTime}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 mt-2 text-xs sm:text-sm text-blue-100">
                      <User className="w-3 h-3 sm:w-4 sm:h-4 text-blue-200" />
                      <span>{selectedArticle.author}</span>
                      <span className="text-blue-200">•</span>
                      <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-blue-200" />
                      <span>{getDisplayDate(selectedArticle)}</span>
                    </div>
                    <div className="flex justify-start mt-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleShareArticle(selectedArticle)}
                        className="hover:bg-white/20 text-white border border-white/30 hover:border-white/50 transition-all duration-200 flex items-center gap-2 text-xs sm:text-sm touch-button"
                      >
                        <Share2 className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="text-xs sm:text-sm">Share Article</span>
                      </Button>
                    </div>
                  </div>
                </div>
              </DialogHeader>

              <ScrollArea className="max-h-[60vh] pr-2 sm:pr-4">
                <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 sm:p-6 shadow-inner border border-blue-100">
                  <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                    {getOverviewDescription(selectedArticle)}
                  </p>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 sm:p-4">
                  <span className="text-xs sm:text-sm font-medium text-blue-800 flex items-center">
                    <Tag className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                    Tags:
                  </span>
                  {(
                    Array.isArray((selectedArticle as any)?.tags)
                      ? (selectedArticle as any).tags
                      : String((selectedArticle as any)?.tags || '')
                        .split(',')
                        .map(t => t.trim())
                        .filter(Boolean)
                  ).map((tag: string, index: number) => (
                    <Badge
                      key={index}
                      variant="outline"
                      className="text-xs bg-blue-100 text-blue-800 border-blue-300 hover:bg-blue-200 transition-colors"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              </ScrollArea>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Image Preview Modal */}
      <Dialog open={!!previewImage} onOpenChange={(open) => setPreviewImage(open ? previewImage : null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden bg-white border shadow-2xl" aria-describedby="image-preview">
          {previewImage && (
            <div className="relative">
              <img src={previewImage} alt="Preview" className="w-full h-auto object-contain max-h-[80vh]" />
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
